define('models/calendar/item',[
    'underscore', 'backbone', 'mustache', 'moment', 'utils',
    'services/api', 'services/i18n', 'services/user',
    'services/events/calendar-events',
    'services/platform.service'
], function(
    // libs
    _, Backbone, Mustache, moment, U,
    // services
    API, i18n, User,
    CalendarEvents,
    PlatformService
) {

    var DEFAULTS = {
            position: 0,
            done: false,
            feedback_level: 0,
            feedback_comment: null,
            group: 0, // FIXME temp default value,
            isVisible: false
        },
        BLACKLIST = ['name', 'user', 'assignedByGroup', '_isPrintable', 'point'],
        FEEDBACK = [
            'calendar:Feedback Default (i feel very good)',
            'calendar:Feedback level 1 (i feel very bad)',
            'calendar:Feedback level 2',
            'calendar:Feedback level 3',
            'calendar:Feedback level 4',
            'calendar:Feedback level 5',
            'calendar:Feedback level 6',
            'calendar:Feedback level 7',
            'calendar:Feedback level 8',
            'calendar:Feedback level 9',
            'calendar:Feedback level 10 (i feel very good)'
        ];

    /**
     * Item appears as an entry in the calendar. Its server-side
     * counterpart is the DayLogEntry and is retrieved as a calendar item
     * via the API.
     *
     * Item is a model base class. USE its specialized class in the item
     * directory.
     */
    var MCalendarItem = Backbone.Epoxy.Model.extend({
        idAttribute: '_id',
        _blacklist: function(defaults, parent) {
            if (parent) return BLACKLIST;
            if (_.isFunction(defaults)) defaults = defaults.call(this);
            return _.union(BLACKLIST, defaults);
        },
        _defaults: function(defaults, parent) {
            if (parent) return DEFAULTS;
            if (_.isFunction(defaults)) defaults = defaults.call(this);
            return _.extend({}, DEFAULTS, defaults);
        },

        constructor: function(attrs, options) {
            this.computeds = _.extend({}, MCalendarItem.prototype.computeds, this.computeds);
            this.defaults = _.wrap(this.defaults, this._defaults);
            this.blacklist = _.wrap(this.blacklist, this._blacklist);
            MCalendarItem.__super__.constructor.apply(this, arguments);
        },

        computeds: {
            assignedBy: {
                deps: ["assignedByGroup"],
                get: function(assignedByGroup) {
                    return assignedByGroup ? assignedByGroup.name : null;
                },
            },
            durationMinutes: {
                deps: ["duration"],
                get: function(duration) {
                    return Math.floor(duration / 60);
                },
                set: function(minutes) {
                    return {
                        duration: minutes * 60
                    };
                }
            },
        },

        // urlRoot: API.url("calendar/items/"),
        urlRoot: function() {
            return API.url(Mustache.render("{{#id}}users/{{id}}/{{/id}}calendar/items/", {
                id: this.get('user')
            }));
        },

        url: function() {
            var url = MCalendarItem.__super__.url.apply(this, arguments);
            if (this.autofixRelated) {
                url = url + '?autofixRelated';
            }
            return url;
        },

        countUrl: function() {
            return API.url("calendar/items/count");
        },

        fetch: function(opt) {
            var options = opt || {};
            if (options.user) this.set('user', options.user, opt);
            return MCalendarItem.__super__.fetch.call(this, opt);
        },

        destroy: function(options) {
            options = options || {};
            var success = options.success,
                context = options.context,
                autofixRelated = options.autofixRelated,
                id = this.id;

            options.success = function() {
                CalendarEvents.itemDestroyed(id);
                if (success) success.apply(context, arguments);
            };

            if (autofixRelated) this.autofixRelated = autofixRelated;

            return MCalendarItem.__super__.destroy.call(this, options);
        },

        save: function(attrs, options) {
            if (options && options.autofixRelated) this.autofixRelated = options.autofixRelated

            if (attrs && attrs.hasOwnProperty('feedback_level') && attrs.hasOwnProperty('feedback_comment')) {
                this.set({ feedbackCount: this.get('feedbackCount') + 1 });
                
                if (this.get('feedbackCount') > 4 && PlatformService.isNative()) {
                    U.displayRatingPopup();
                }
            }

            return MCalendarItem.__super__.save.call(this, attrs, options);
        },

        updatePosition: function(position, date, options) {
            options = options || {};
            this.set({ position: position, date: date }, options);
            // save if changed
            if (this.hasChanged() && options.sync !== false) {
                this.save(
                    this.changedAttributes(),
                    _.extend({ patch: true }, options)
                );
            }
        },

        resetToNew: function(user, momentDate, options) {
            return this.reset({
                user: user,
                date: API.formatDate(momentDate)
            }, options);
        },

        resetToId: function(id, user, options) {
            var attrs = { user: user };
            attrs[this.idAttribute] = id;
            return this.reset(attrs, options);
        },

        hasPassed: function(momentDate) {
            return !(momentDate || this.getDate()).isAfter(new Date(), 'day');
        },

        isInFuture: function() {
            return this.getDate().isAfter(moment());
        },

        hasComment: function() {
            return Boolean(this.get('feedback_comment'));
        },

        isDone: function() {
            return this.get('done');
        },

        isReferencedInUrl: function() {
            return Backbone.history.getFragment().indexOf(this.id) > -1;
        },

        isAfterDays: function(days) {
            return moment(this.get('date')).isAfter(moment().add(days, 'days'));
        },

        /**
         * This must be overriden by child classes to be able to create new favorites.
         * @return  {Object} attributes - to be saved as a favorite
         */
        getFavoriteData: _.noop,

        ///////////////
        // Accessors //
        ///////////////
        setDate: function(momentDate, options) {
            return this.set({ date: API.formatDate(momentDate) }, options);
        },
        getDate: function() {
            return moment(this.get('date'));
        },

        toggleDone: function(value, options) {
            return this.set({
                done: _.isUndefined(value) ? !this.get('done') : Boolean(value)
            }, options);
        },

        setFeedbackLevel: function(value, options) {
            return this.set({ feedback_level: Number(value) }, options);
        },

        getFeedbackLevel: function() {
            return this.get('feedback_level') !== null ? this.get('feedback_level') : 10;
        },

        getFeedbackComment: function() {
            return this.get('feedback_comment');
        },

        getFeedbackLevelTitle: function(value) {
            return i18n.t(FEEDBACK[value || this.getFeedbackLevel()]);
        },

        getType: function() {
            return this.get('type');
        },

        getName: function() {
            return this.get('name');
        },

        // TODO: Change this when we will be receving the customData the way we wants it
        getFilterClass: function() {
            var filterClass = 'type-' + this.get('type');
            if (this.get('type') === 'custom_data') {
                filterClass += '-' + this.get('customData');
            }
            return filterClass;
        },

        getDescription: _.noop,
        getDisplayTitle: function() {
            return this.get('name');
        },

    });

    // Return the model for the module
    return MCalendarItem;
});

